/**
 * Filtros avançados da aba Grupos — a conta e o recorte, sem React.
 *
 * A tela existe para uma pergunta só: "qual grupo entrega o crédito que o meu
 * cliente quer RECEBER, pelo menor lance?". Os filtros que sobraram aqui são
 * os que respondem isso; os que descreviam o grupo (contemplados, assembleias
 * realizadas) saíram porque não ajudam a escolher — o Ajudante de lance logo
 * abaixo da lista já ordena por chance de contemplar. Vagas voltou: grupo sem
 * vaga não vende, então é recorte de escolha, não descrição.
 *
 * Está separado de `index.tsx` porque "crédito" muda de significado conforme o
 * modo de lance, e regra que muda de significado precisa de teste numérico
 * direto — não de um clique na tela.
 */

/** As duas modalidades de leilão do motor v5, mais "sem recorte". */
export type ModoLance = 'todos' | 'LANCE LIVRE' | 'SEM EMBUTIDO';

export const ROTULO_MODO_LANCE: Record<ModoLance, string> = {
  todos: 'Todos os modos',
  'LANCE LIVRE': 'Lance livre',
  'SEM EMBUTIDO': 'Lance livre sem embutido',
};

/**
 * Embutido assumido quando o grupo não publica o próprio.
 *
 * A Servopa pratica 30% na maior parte das cartas, e
 * `servopa_group_details.lance_embutido` nasce 0 (DEFAULT da migration 04)
 * enquanto o scraper não leu o regulamento daquele grupo — 0 ali significa
 * "ainda não sei", não "este grupo não embute". Assumir o 0 literal faria
 * bruto = líquido e o vendedor prometeria um crédito que a carta não entrega.
 */
export const EMBUTIDO_PADRAO_PCT = 30;

/**
 * O embutido do grupo, ou o padrão quando ele não informa um valor utilizável.
 *
 * Descarta 0, negativo e >= 100 pelo mesmo motivo: nenhum deles é um embutido
 * real, e 100 ainda estouraria a divisão do bruto.
 */
export const percentualEmbutido = (valor: number | string | null | undefined): number => {
  const n = typeof valor === 'string' ? Number.parseFloat(valor.replace(',', '.')) : valor;
  if (n == null || !Number.isFinite(n) || n <= 0 || n >= 100) return EMBUTIDO_PADRAO_PCT;
  return n;
};

/**
 * Nos DOIS leilões livres o campo Crédito é lido como crédito LÍQUIDO.
 *
 * Decisão do dono do produto: a venda dos dois é fechada sobre a carta cheia,
 * que já traz o embutido abatido — o que o cliente recebe na mão é sempre o
 * líquido. Só nos demais modos (fixo, fidelidade) o crédito digitado é o mesmo
 * crédito da cota.
 */
export const modoUsaCreditoLiquido = (modo: ModoLance): boolean =>
  modo === 'LANCE LIVRE' || modo === 'SEM EMBUTIDO';

/**
 * O crédito BRUTO que precisa ser contratado para o cliente receber `liquido`.
 *
 *     bruto = liquido / (1 - embutido)
 *
 * Ex.: 260.000 líquidos com 30% de embutido → 371.428,57 de carta.
 */
export const creditoBrutoNecessario = (liquido: number, embutidoPct: number): number =>
  liquido / (1 - percentualEmbutido(embutidoPct) / 100);

/** O que basta saber do grupo para decidir se ele atende o crédito pedido. */
export interface GrupoParaFiltro {
  credit_min: number | null;
  credit_max: number | null;
  /** `servopa_group_details.lance_embutido` em %; ausente quando não carregou. */
  lance_embutido_pct?: number | string | null;
  /** Vagas abertas no grupo; ausente quando não se sabe. */
  vagas?: number | null;
}

/** Os campos dos filtros avançados, como saem dos inputs (texto). */
export interface FiltrosAvancados {
  /** Crédito que o cliente quer RECEBER (R$). Vazio = sem filtro. */
  credito: string;
  modoLance: ModoLance;
  /** Tetos de lance em % do crédito. Vazio = sem filtro. */
  maxLanceMedio: string;
  maxLanceMin: string;
  /** Teto de parcela (R$). */
  maxParcela: string;
  /** Mínimo de vagas abertas no grupo. Vazio = sem filtro. */
  minVagas: string;
}

export const FILTROS_AVANCADOS_VAZIOS: FiltrosAvancados = {
  credito: '',
  modoLance: 'todos',
  maxLanceMedio: '',
  maxLanceMin: '',
  maxParcela: '',
  minVagas: '',
};

/** Texto de input → número, ou null quando o campo está vazio/ilegível. */
export const numeroOuNulo = (texto: string): number | null => {
  if (!texto.trim()) return null;
  const n = Number.parseFloat(texto.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/**
 * O crédito de carta que o grupo precisa oferecer, dado o que o cliente quer
 * receber. Null quando o campo está vazio.
 */
export const creditoAlvoDoGrupo = (
  filtros: FiltrosAvancados,
  grupo: GrupoParaFiltro,
): number | null => {
  const desejado = numeroOuNulo(filtros.credito);
  if (desejado == null) return null;
  if (!modoUsaCreditoLiquido(filtros.modoLance)) return desejado;
  return creditoBrutoNecessario(desejado, percentualEmbutido(grupo.lance_embutido_pct));
};

/** O que a tela sabe do grupo além do cadastro dele. */
export interface ContextoDoGrupo {
  /**
   * Histórico de lance com amostra suficiente, ou null quando não sustenta
   * (ver MIN_ASSEMBLEIAS em lanceRanking).
   */
  historico: { lance_medio_pct: number | null; lance_min_pct: number | null } | null;
  /** Faixa de parcela das categorias, ou null quando ainda não carregou. */
  parcelas: { min: number; max: number } | null;
}

/**
 * O grupo sobrevive aos filtros avançados?
 *
 * Grupo SEM histórico suficiente sai da lista quando qualquer teto de lance
 * está ligado: não dá pra afirmar que ele cabe num teto que nunca foi medido —
 * "não sei" e "é barato" não podem cair no mesmo balde.
 */
export const grupoPassaNosFiltrosAvancados = (
  grupo: GrupoParaFiltro,
  filtros: FiltrosAvancados,
  contexto: ContextoDoGrupo,
): boolean => {
  const tetoMedio = numeroOuNulo(filtros.maxLanceMedio);
  const tetoMin = numeroOuNulo(filtros.maxLanceMin);
  if (tetoMedio != null || tetoMin != null) {
    const hist = contexto.historico;
    if (!hist) return false;
    if (tetoMedio != null && (hist.lance_medio_pct ?? Infinity) > tetoMedio) return false;
    if (tetoMin != null && (hist.lance_min_pct ?? Infinity) > tetoMin) return false;
  }

  // Crédito: o alvo tem que caber na faixa de cartas do grupo. Faixa aberta de
  // um lado (null) não reprova — é dado que falta, não porta fechada.
  const alvo = creditoAlvoDoGrupo(filtros, grupo);
  if (alvo != null) {
    if (grupo.credit_min != null && alvo < grupo.credit_min) return false;
    if (grupo.credit_max != null && alvo > grupo.credit_max) return false;
  }

  const tetoParcela = numeroOuNulo(filtros.maxParcela);
  if (tetoParcela != null) {
    // Sem as categorias carregadas não há como afirmar que cabe no bolso.
    if (!contexto.parcelas || contexto.parcelas.min > tetoParcela) return false;
  }

  // Vagas desconhecidas não passam: não dá pra prometer vaga que não se viu.
  const pisoVagas = numeroOuNulo(filtros.minVagas);
  if (pisoVagas != null && (grupo.vagas ?? -Infinity) < pisoVagas) return false;

  return true;
};
